/**
 * Unified Email Verification Service
 * Centralizes email verification for both hover and beta platforms
 * Uses Supabase Auth as the single source of truth for email verification
 */

import { supabase } from '../lib/supabase';
import { betaAuthService } from './BetaAuthService';

interface VerificationResult {
  success: boolean;
  message: string;
  verified?: boolean;
  email?: string;
}

export class UnifiedEmailVerificationService {
  private verificationBaseUrl: string;

  constructor() {
    // Use hover as the central verification hub
    this.verificationBaseUrl = window.location.origin;
  }

  /**
   * Send verification email for new user registration
   * This is the single method both platforms use
   */
  async sendVerificationEmail(email: string, options?: {
    redirectTo?: string;
    platform?: 'hover' | 'beta';
    metadata?: Record<string, any>;
  }): Promise<VerificationResult> {
    try {
      // Default redirect is back to hover
      const redirectUrl = options?.redirectTo || `${this.verificationBaseUrl}/auth/verify`;
      
      // Include platform information in the redirect URL
      const url = new URL(redirectUrl);
      if (options?.platform) {
        url.searchParams.append('platform', options.platform);
      }
      url.searchParams.append('email', email);

      // Send verification email via Supabase
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: url.toString(),
          data: {
            platform: options?.platform || 'hover',
            ...options?.metadata
          }
        }
      });

      if (error) {
        return {
          success: false,
          message: `Failed to send verification email: ${error.message}`
        };
      }

      // Log verification request for both platforms
      await this.logVerificationRequest(email, options?.platform || 'hover');

      return {
        success: true,
        message: 'Verification email sent! Please check your inbox.',
        email
      };
    } catch (error: any) {
      console.error('Verification email error:', error);
      return {
        success: false,
        message: 'Failed to send verification email. Please try again.'
      };
    }
  }

  /**
   * Verify email token from verification link
   * Handles verification for both platforms
   */
  async verifyEmailToken(token: string, type: string = 'email'): Promise<VerificationResult> {
    try {
      // Verify the token with Supabase
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: type as any
      });

      if (error) {
        return {
          success: false,
          message: `Verification failed: ${error.message}`
        };
      }

      if (data.user) {
        // Update user's email verification status
        await this.updateVerificationStatus(data.user.email!, true);
        
        // Sync verification status with beta if needed
        await this.syncVerificationWithBeta(data.user.email!, true);

        return {
          success: true,
          message: 'Email verified successfully!',
          verified: true,
          email: data.user.email
        };
      }

      return {
        success: false,
        message: 'Invalid verification token'
      };
    } catch (error: any) {
      console.error('Email verification error:', error);
      return {
        success: false,
        message: 'Verification failed. Please request a new verification email.'
      };
    }
  }

  /**
   * Check if an email is verified
   * Single source of truth for both platforms
   */
  async checkEmailVerificationStatus(email: string): Promise<{
    verified: boolean;
    platform?: string;
    lastVerified?: string;
  }> {
    try {
      // First check Supabase (primary source)
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user && user.email === email) {
        return {
          verified: user.email_confirmed_at !== null,
          platform: 'hover',
          lastVerified: user.email_confirmed_at || undefined
        };
      }

      // Check if email exists in our verification records
      const { data, error } = await supabase
        .from('email_verifications')
        .select('verified, platform, verified_at')
        .eq('email', email)
        .single();

      if (data) {
        return {
          verified: data.verified,
          platform: data.platform,
          lastVerified: data.verified_at
        };
      }

      // Email not found, not verified
      return {
        verified: false
      };
    } catch (error) {
      console.error('Error checking verification status:', error);
      return {
        verified: false
      };
    }
  }

  /**
   * Resend verification email
   */
  async resendVerificationEmail(email: string): Promise<VerificationResult> {
    try {
      // Check if already verified
      const status = await this.checkEmailVerificationStatus(email);
      
      if (status.verified) {
        return {
          success: false,
          message: 'Email is already verified!',
          verified: true,
          email
        };
      }

      // Resend verification email
      return await this.sendVerificationEmail(email, {
        platform: status.platform as 'hover' | 'beta' || 'hover'
      });
    } catch (error: any) {
      console.error('Resend verification error:', error);
      return {
        success: false,
        message: 'Failed to resend verification email'
      };
    }
  }

  /**
   * Update verification status in database
   */
  private async updateVerificationStatus(email: string, verified: boolean): Promise<void> {
    try {
      // Update or insert verification record
      await supabase
        .from('email_verifications')
        .upsert({
          email,
          verified,
          verified_at: verified ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'email'
        });
    } catch (error) {
      console.error('Error updating verification status:', error);
    }
  }

  /**
   * Sync verification status with beta platform
   */
  private async syncVerificationWithBeta(email: string, verified: boolean): Promise<void> {
    try {
      // Check if user exists on beta
      const betaStatus = await betaAuthService.checkBetaSession(email);
      
      if (betaStatus.authenticated || betaStatus.user) {
        // Send verification status to beta
        const response = await fetch(`${import.meta.env.VITE_BETA_URL}/api/auth/verify-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Source': 'hover',
            'X-Verification-Sync': 'true'
          },
          body: JSON.stringify({
            email,
            verified,
            verified_at: new Date().toISOString(),
            source: 'hover'
          })
        });

        if (!response.ok) {
          console.log('Beta verification sync failed, but continuing');
        }
      }
    } catch (error) {
      // Non-critical error, log but don't fail
      console.log('Beta sync error (non-critical):', error);
    }
  }

  /**
   * Log verification request for analytics
   */
  private async logVerificationRequest(email: string, platform: string): Promise<void> {
    try {
      await supabase
        .from('verification_logs')
        .insert({
          email,
          platform,
          action: 'verification_sent',
          timestamp: new Date().toISOString()
        });
    } catch (error) {
      // Non-critical, just log
      console.log('Failed to log verification request:', error);
    }
  }

  /**
   * Handle verification callback from email link
   */
  async handleVerificationCallback(params: URLSearchParams): Promise<VerificationResult> {
    const token = params.get('token');
    const type = params.get('type') || 'email';
    const email = params.get('email');
    const platform = params.get('platform');

    if (!token) {
      return {
        success: false,
        message: 'No verification token provided'
      };
    }

    const result = await this.verifyEmailToken(token, type);

    if (result.success && email) {
      // Log successful verification
      await supabase
        .from('verification_logs')
        .insert({
          email,
          platform: platform || 'hover',
          action: 'verification_completed',
          timestamp: new Date().toISOString()
        });
    }

    return result;
  }

  /**
   * Create unified verification API endpoint for beta to use
   */
  async createVerificationEndpoint(): Promise<string> {
    // This returns the endpoint URL that beta can use
    return `${this.verificationBaseUrl}/api/verify/send`;
  }

  /**
   * Validate email format before sending verification
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

// Export singleton instance
export const emailVerificationService = new UnifiedEmailVerificationService();